import type { ReactNode } from 'react';
import '../../job-roles/job-roles.css';
import '../benefits-deductions.css';

export interface CatalogRow {
  code: string;
  name: string;
  description: string;
  isHidden: boolean;
  /** Short tags shown under the description (a payroll-deduction hint, "Leave", "Fixed amount" ...). */
  tags: string[];
  /** The standing note some types carry (the Annual Bonus Plan's earning-code note). */
  note?: string | null;
  /** True for an "Other ..." item: its button says "Add custom" and opens the custom form pre-set to the category. */
  opensCustomForm: boolean;
}

/**
 * The system catalog for one category: each type with its plain-language description, an Add button (add from the catalog, prefilled), and a hide / show
 * control. Hiding is per company and never deletes anything. Write controls are omitted when the page is read-only. `noun` is "benefit" or "deduction".
 */
export function CatalogList({ idPrefix, noun, rows, readOnly, busyCode, showHidden, onShowHidden, onAdd, onToggleHidden, emptyMessage, children }: {
  idPrefix: string;
  noun: string;
  rows: CatalogRow[];
  readOnly: boolean;
  busyCode: string | null;
  showHidden: boolean;
  onShowHidden: (value: boolean) => void;
  onAdd: (row: CatalogRow) => void;
  onToggleHidden: (row: CatalogRow) => void;
  emptyMessage: string;
  children?: ReactNode;
}) {
  return (
    <>
      {children}
      {rows.length === 0 && !showHidden ? (
        <p className="muted" data-testid="catalog-empty">{emptyMessage}</p>
      ) : (
        <>
          <label className="bd-toggle-row" htmlFor={`${idPrefix}-show-hidden`}>
            <input id={`${idPrefix}-show-hidden`} type="checkbox" checked={showHidden} onChange={(e) => onShowHidden(e.target.checked)} />
            Show types I have hidden
          </label>
          <ul className="bd-catalog" aria-label={`Available ${noun} types`}>
            {rows.map((row) => (
              <li key={row.code} className={row.isHidden ? 'bd-catalog__item bd-catalog__item--hidden' : 'bd-catalog__item'}>
                <h4 className="bd-catalog__name">{row.name}{row.isHidden && <span className="bd-tag"> Hidden</span>}</h4>
                <p className="bd-catalog__text">{row.description}</p>
                {row.note && <p className="bd-help">{row.note}</p>}
                {row.tags.length > 0 && (
                  <div className="bd-catalog__tags">{row.tags.map((t) => <span key={t} className="bd-tag">{t}</span>)}</div>
                )}
                {!readOnly && (
                  <div className="bd-catalog__buttons">
                    {!row.isHidden && (
                      <button type="button" className="jr-btn jr-btn--primary jr-btn--small" aria-label={`${row.opensCustomForm ? 'Add custom' : 'Add'} ${row.name}`} disabled={busyCode === row.code} onClick={() => onAdd(row)}>
                        <span aria-hidden="true">+ </span>{row.opensCustomForm ? 'Add custom' : 'Add'}
                      </button>
                    )}
                    <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" aria-label={`${row.isHidden ? 'Show' : 'Hide'} ${row.name}`} disabled={busyCode === row.code} onClick={() => onToggleHidden(row)}>
                      {row.isHidden ? 'Show' : 'Hide'}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}
